import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import adminService from "../../services/adminService";

const SERVICES = [
  { key: "circuit", label: "Circuits", emoji: "🗺️" },
  { key: "hotel", label: "Hôtels", emoji: "🏨" },
  { key: "transport", label: "Transports", emoji: "🚌" },
  { key: "visite", label: "Visites guidées", emoji: "👁️" },
  { key: "voiture", label: "Voitures", emoji: "🚗" },
];

const MODE_INFO = {
  direct: {
    label: "Paiement direct",
    desc: "Le client paie immédiatement via CMI lors de la réservation.",
    cls: "from-emerald-500 to-emerald-700",
  },
  email: {
    label: "Paiement par email",
    desc: "Le client envoie une demande. Vous fixez le prix et envoyez un lien de paiement par email.",
    cls: "from-blue-500 to-indigo-700",
  },
};

const SettingsManager = () => {
  const [paymentMode, setPaymentMode] = useState(null);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let alive = true;
    adminService
      .getSettings()
      .then((res) => {
        if (!alive) return;
        setPaymentMode(res?.data?.paymentMode || {});
        setAiEnabled(!!res?.data?.aiEnabled);
      })
      .catch((e) => toast.error(e.message || "Erreur de chargement des paramètres"))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const setMode = (svc, mode) => {
    setPaymentMode((p) => ({ ...p, [svc]: mode }));
    setDirty(true);
  };

  const toggleAi = () => {
    setAiEnabled((v) => !v);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await adminService.updateSettings({ paymentMode, aiEnabled });
      toast.success("Paramètres enregistrés ✅");
      setDirty(false);
    } catch (e) {
      toast.error(e.message || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <style>{`.nz-skel{position:relative;overflow:hidden;background:#eef2f7}.nz-skel::after{content:"";position:absolute;inset:0;transform:translateX(-100%);background:linear-gradient(90deg,transparent,rgba(255,255,255,.7),transparent);animation:nz-shimmer 1.4s infinite}@keyframes nz-shimmer{100%{transform:translateX(100%)}}`}</style>
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-white rounded-3xl border border-slate-100 p-5 h-24 nz-skel" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-5">
        <h2 className="text-2xl font-black text-slate-800 flex items-center gap-2">⚙️ Paramètres de paiement</h2>
        <p className="text-sm text-slate-500 mt-1">
          Pour chaque service, choisissez comment le client règle sa réservation.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {SERVICES.map((svc) => {
          const current = paymentMode?.[svc.key] || "direct";
          return (
            <div key={svc.key} className="bg-white rounded-3xl shadow-sm border border-slate-100 p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-2xl bg-slate-100 flex items-center justify-center text-xl">{svc.emoji}</div>
                <div>
                  <p className="font-black text-slate-800">{svc.label}</p>
                  <p className="text-xs text-slate-400">Mode actuel : {MODE_INFO[current].label}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {["direct", "email"].map((mode) => {
                  const active = current === mode;
                  return (
                    <button
                      key={mode}
                      onClick={() => setMode(svc.key, mode)}
                      className={`text-left rounded-2xl border-2 p-3 transition-all ${
                        active ? "border-transparent text-white shadow-lg" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                      }`}
                      style={active ? undefined : {}}
                    >
                      <div className={active ? `rounded-xl bg-gradient-to-br ${MODE_INFO[mode].cls} -m-3 p-3` : ""}>
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-sm">{mode === "direct" ? "⚡ Direct" : "✉️ Email"}</span>
                          {active && <span className="text-xs">✓</span>}
                        </div>
                        <p className={`text-[11px] mt-1.5 leading-snug ${active ? "text-white/90" : "text-slate-400"}`}>
                          {MODE_INFO[mode].desc}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Fonctionnalités IA */}
      <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center text-xl text-white flex-none">✨</div>
            <div className="min-w-0">
              <p className="font-black text-slate-800">Fonctionnalités IA</p>
              <p className="text-xs text-slate-500 mt-0.5 max-w-xl">
                Dictée vocale + remplissage automatique des formulaires par l'IA. Désactivées, elles sont
                <strong> masquées partout</strong> sur le site. Activées, elles apparaissent dans tous les formulaires.
              </p>
            </div>
          </div>

          <button
            type="button"
            role="switch"
            aria-checked={aiEnabled}
            onClick={toggleAi}
            className={`relative inline-flex h-8 w-14 flex-none items-center rounded-full transition-colors ${aiEnabled ? "bg-emerald-500" : "bg-slate-300"}`}
          >
            <span className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform ${aiEnabled ? "translate-x-7" : "translate-x-1"}`} />
          </button>
        </div>
        <p className={`mt-3 text-xs font-bold ${aiEnabled ? "text-emerald-600" : "text-slate-400"}`}>
          {aiEnabled ? "● Activées — visibles sur le site" : "○ Désactivées — masquées sur le site"}
        </p>
      </div>

      <div className="sticky bottom-4 flex justify-end">
        <button
          onClick={save}
          disabled={!dirty || saving}
          className={`px-6 py-3 rounded-2xl font-bold shadow-lg transition-all ${
            !dirty || saving
              ? "bg-slate-300 text-white cursor-not-allowed"
              : "bg-gradient-to-r from-amber-500 to-orange-600 text-white hover:from-amber-600 hover:to-orange-700"
          }`}
        >
          {saving ? "Enregistrement…" : dirty ? "💾 Enregistrer les modifications" : "✓ À jour"}
        </button>
      </div>
    </div>
  );
};

export default SettingsManager;
